import axios from "axios";
import { setUser, getUser } from "../user";
import { getSavedArticleIds } from "../articles/save";
import { getFilterArticles } from "../articles/filter"; // eslint-disable-line no-unused-vars
import { getChart } from "../chart"; // eslint-disable-line no-unused-vars
import analytics from "../../utils/analytics";
import { prepChartDownload } from "../download/chart";

// Global axios config
const req = axios.create({
  timeout: 10000,
  headers: { "x-api-key": "ZXybxPpalb82ygfwt2Oxy8qtM6D9Ozp82xWs5nEq" }
});

/**
 * This list is used to map modals to different components and / or actions.
 */
export const modals = {
  DOWNLOAD_SAVED_ARTICLES: Symbol("DOWNLOAD_SAVED_ARTICLES"),
  DOWNLOAD_FILTERED_ARTICLES: Symbol("DOWNLOAD_FILTERED_ARTICLES"),
  DOWNLOAD_REPORT: Symbol("DOWNLOAD_REPORT"),
  DOWNLOAD_CHART: Symbol("DOWNLOAD_CHART"),
  DOWNLOAD_SUCCESS: Symbol("DOWNLOAD_SUCCESS"),
  DOWNLOAD_ERROR: Symbol("DOWNLOAD_ERROR")
};

// Actions
const typePrefix = "modal/";
const SHOW = `${typePrefix}SHOW`;
const HIDE = `${typePrefix}HIDE`;

// Selectors

export const getModal = state => state.modal;
export const getModalType = state => state.modal.modalType;
export const getModalProps = state => state.modal.modalProps;

// Action creators

export const showModal = ({ modalType, modalProps }) => ({
  type: SHOW,
  modalType,
  modalProps
});

export const hideModal = () => ({ type: HIDE });

/**
 * Download requests typically happen as part of a modal flow. Decide what
 * should happen for a particular download request and what should happen
 * after the request is fulfilled.
 *
 * @param {*} user
 */
export const requestDownload = user => (dispatch, getState) => {
  dispatch(setUser(user));
  const state = getState();
  const modalType = getModalType(state);
  let nextAction = showModal({ modalType: modals.DOWNLOAD_ERROR });
  let modalProps = {};
  switch (modalType) {
    case modals.DOWNLOAD_SAVED_ARTICLES:
      nextAction = requestSavedArticlesDownload;
      break;
    case modals.DOWNLOAD_FILTERED_ARTICLES:
      nextAction = requestFilteredArticlesDownload;
      break;
    case modals.DOWNLOAD_REPORT:
      nextAction = requestReportDownload;
      modalProps.text =
        "Your download will start in a few moments. Check your browser's download folder.";
      break;
    case modals.DOWNLOAD_CHART:
      nextAction = prepChartDownload;
      break;
  }
  dispatch(nextAction());
};

// Make a request to mailchimp
export const mailchimpRequest = (email, wantsNewsletter) => {
  const MAILCHIMP_NEWSLETTER =
    "https://4xin66b3g0.execute-api.eu-west-1.amazonaws.com/prod/newsletter";
  const MAILCHIMP_NO_NEWSLETTER =
    "https://4xin66b3g0.execute-api.eu-west-1.amazonaws.com/prod/email";
  const signupUrl = wantsNewsletter
    ? MAILCHIMP_NEWSLETTER
    : MAILCHIMP_NO_NEWSLETTER;
  return req({
    data: { email },
    method: "post",
    url: signupUrl
  })
    .then(res => res)
    .catch(err => {
      // Sometimes these are invalid/duplicate address errors, sometimes other
      // errors. We can't tell the difference for now, so just ignore.
      console.log("err", err); // eslint-disable-line no-console
    });
};

// Make a request for a pdf
const customPdfRequest = ids =>
  req({
    data: { ids },
    method: "post",
    url: "https://4xin66b3g0.execute-api.eu-west-1.amazonaws.com/prod"
  })
    .then(res => {
      let body;
      if (res.status === 200) {
        body = JSON.parse(res.data.body);
        if (body.message === "success" && body.file)
          window.location.href = body.file;
      }
      return status;
    })
    .catch(err => {
      return Promise.reject(err);
    });

// eslint-disable-next-line no-unused-vars
export const requestSavedArticlesDownload = () => (dispatch, getState) => {
  const state = getState();
  const savedArticleIds = getSavedArticleIds(state).filter(item => {
    if (item) {
      return item;
    }
  });
  const user = getUser(state);

  let nextModal = showModal({
    modalType: modals.DOWNLOAD_SUCCESS,
    modalProps: {
      text:
        "Your download will start in a few moments. Check your browser's download folder."
    }
  });

  axios
    .all([
      mailchimpRequest(user.email, user.newsletter),
      customPdfRequest(savedArticleIds.map(id => `${id}`))
    ])
    .then(() => {
      dispatch(nextModal);
      analytics("send", "event", "download", "downloadSavedArticles", {
        articleIds: savedArticleIds
      });
    })
    .catch(err => {
      console.log("err", err); // eslint-disable-line no-console
      dispatch(showModal({ modalType: modals.DOWNLOAD_ERROR }));
    });
};

// eslint-disable-next-line no-unused-vars
export const requestFilteredArticlesDownload = () => (dispatch, getState) => {
  const state = getState();
  const articleIds = getFilterArticles(state).filter(item => {
    if (item) {
      return item;
    }
  });
  const user = getUser(state);

  let nextModal = showModal({
    modalType: modals.DOWNLOAD_SUCCESS,
    modalProps: {
      text:
        "Your download will start in a few moments. Check your browser's download folder."
    }
  });

  axios
    .all([
      mailchimpRequest(user.email, user.newsletter),
      customPdfRequest(articleIds.map(id => `${id}`))
    ])
    .then(() => {
      dispatch(nextModal);
      analytics("send", "event", "download", "downloadFilteredArticles", {
        articleIds
      });
    })
    .catch(err => {
      console.log("err", err); // eslint-disable-line no-console
      dispatch(showModal({ modalType: modals.DOWNLOAD_ERROR }));
    });
};

// eslint-disable-next-line no-unused-vars
export const requestReportDownload = () => (dispatch, getState) => {
  const user = getUser(getState());
  mailchimpRequest(user.email, user.newsletter).then(() => {
    window.location.href =
      "https://s3.eu-west-2.amazonaws.com/soet-pdf/State_of_European_Tech_2017_FullReport.pdf";
    dispatch(
      showModal({
        modalType: modals.DOWNLOAD_SUCCESS,
        modalProps: {
          text:
            "Your download will start in a few moments. Check your browser's download folder."
        }
      })
    );
    analytics("send", "event", "download", "downloadFullReport");
  });
};

// Reducer

const initialState = {
  isOpen: false,
  modalType: null,
  modalProps: {}
};

export function modalReducer(state = initialState, action) {
  switch (action.type) {
    case SHOW:
      return {
        isOpen: true,
        modalType: action.modalType,
        modalProps: action.modalProps
      };
    case HIDE:
      return { ...state, isOpen: false };
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/state/modal/index.js